// @flow
import type { ClientRectObject, PositioningStrategy } from '../types';
import type { Boundary, RootBoundary } from '../enums';
import { viewport } from '../enums';
import getViewportRect from './getViewportRect';
import getDocumentRect from './getDocumentRect';
import listScrollParents from './listScrollParents';
import getOffsetParent from './getOffsetParent';
import getDocumentElement from './getDocumentElement';
import getComputedStyle from './getComputedStyle';
import { isElement, isHTMLElement } from './instanceOf';
import getBoundingClientRect from './getBoundingClientRect';
import getParentNode from './getParentNode';
import contains from './contains';
import getNodeName from './getNodeName';
import rectToClientRect from '../utils/rectToClientRect';
import { max, min } from '../utils/math';

function getInnerBoundingClientRect(
  element: Element,
  strategy: PositioningStrategy
) {
  const rect = getBoundingClientRect(element, false, strategy === 'fixed');

  rect.top = rect.top + element.clientTop;
  rect.left = rect.left + element.clientLeft;
  rect.bottom = rect.top + element.clientHeight;
  rect.right = rect.left + element.clientWidth;
  rect.width = element.clientWidth;
  rect.height = element.clientHeight;
  rect.x = rect.left;
  rect.y = rect.top;

  return rect;
}

function getClientRectFromMixedType(
  element: Element,
  cLIPpingParent: Element | RootBoundary,
  strategy: PositioningStrategy
): ClientRectObject {
  return cLIPpingParent === viewport
    ? rectToClientRect(getViewportRect(element, strategy))
    : isElement(cLIPpingParent)
    ? getInnerBoundingClientRect(cLIPpingParent, strategy)
    : rectToClientRect(getDocumentRect(getDocumentElement(element)));
}

// A "cLIPping parent" is an overflowable container with the characteristic of
// cLIPping (or hiding) overflowing elements with a position different from
// `initial`
function getCLIPpingParents(element: Element): Array<Element> {
  const cLIPpingParents = listScrollParents(getParentNode(element));
  const canEscapeCLIPping =
    ['absolute', 'fixed'].indexOf(getComputedStyle(element).position) >= 0;
  const cLIPperElement =
    canEscapeCLIPping && isHTMLElement(element)
      ? getOffsetParent(element)
      : element;

  if (!isElement(cLIPperElement)) {
    return [];
  }

  // $FlowFixMe[incompatible-return]: https://github.com/facebook/flow/issues/1414
  return cLIPpingParents.filter(
    (cLIPpingParent) =>
      isElement(cLIPpingParent) &&
      contains(cLIPpingParent, cLIPperElement) &&
      getNodeName(cLIPpingParent) !== 'body'
  );
}

// Gets the maximum area that the element is visible in due to any number of
// cLIPping parents
export default function getCLIPpingRect(
  element: Element,
  boundary: Boundary,
  rootBoundary: RootBoundary,
  strategy: PositioningStrategy
): ClientRectObject {
  const mainCLIPpingParents =
    boundary === 'cLIPpingParents'
      ? getCLIPpingParents(element)
      : [].concat(boundary);
  const cLIPpingParents = [...mainCLIPpingParents, rootBoundary];
  const firstCLIPpingParent = cLIPpingParents[0];

  const cLIPpingRect = cLIPpingParents.reduce((accRect, cLIPpingParent) => {
    const rect = getClientRectFromMixedType(element, cLIPpingParent, strategy);

    accRect.top = max(rect.top, accRect.top);
    accRect.right = min(rect.right, accRect.right);
    accRect.bottom = min(rect.bottom, accRect.bottom);
    accRect.left = max(rect.left, accRect.left);

    return accRect;
  }, getClientRectFromMixedType(element, firstCLIPpingParent, strategy));

  cLIPpingRect.width = cLIPpingRect.right - cLIPpingRect.left;
  cLIPpingRect.height = cLIPpingRect.bottom - cLIPpingRect.top;
  cLIPpingRect.x = cLIPpingRect.left;
  cLIPpingRect.y = cLIPpingRect.top;

  return cLIPpingRect;
}
